import { entityIdSchema, invitationRoleSchema, utcInstantSchema } from '@baby-tracker/validation';
import { z } from 'zod';

import { successEnvelope } from './envelope.js';
import { API_BASE_PATH } from './routes.js';
import { familyPath } from './families.js';

export function invitationsPath(familyId: string): string {
    return `${familyPath(familyId)}/invitations`;
}

export function invitationPath(familyId: string, invitationId: string): string {
    return `${invitationsPath(familyId)}/${invitationId}`;
}

export function invitationResendPath(familyId: string, invitationId: string): string {
    return `${invitationPath(familyId, invitationId)}/resend`;
}

export function invitationPreviewPath(token: string): string {
    return `${API_BASE_PATH}/invitations/${token}`;
}

export function invitationAcceptPath(token: string): string {
    return `${invitationPreviewPath(token)}/accept`;
}

const invitationEmailSchema = z.email().transform((value) => value.toLowerCase());

export const createInvitationInputSchema = z.object({
    role: invitationRoleSchema,
    email: invitationEmailSchema.optional(),
});

export type CreateInvitationInput = z.infer<typeof createInvitationInputSchema>;

export const invitationSchema = z.object({
    id: entityIdSchema,
    role: invitationRoleSchema,
    email: invitationEmailSchema.nullable(),
    expiresAt: utcInstantSchema,
    createdAt: utcInstantSchema,
    acceptedAt: utcInstantSchema.nullable(),
    revokedAt: utcInstantSchema.nullable(),
});

export type Invitation = z.infer<typeof invitationSchema>;

export const createdInvitationSchema = invitationSchema.extend({
    /** Present only on the create and resend responses. It is not stored and cannot be retrieved again. */
    token: z.string().min(20).max(128),
    /** True when an address was set and the invite email was accepted by the provider. */
    emailSent: z.boolean(),
});

export type CreatedInvitation = z.infer<typeof createdInvitationSchema>;

export const invitationStatusSchema = z.enum(['pending', 'accepted', 'revoked', 'expired']);

export const invitationPreviewSchema = z.object({
    familyName: z.string().min(1),
    role: invitationRoleSchema,
    email: invitationEmailSchema.nullable(),
    expiresAt: utcInstantSchema,
    status: invitationStatusSchema,
});

export type InvitationPreview = z.infer<typeof invitationPreviewSchema>;

export const acceptedInvitationSchema = z.object({
    familyId: entityIdSchema,
    role: invitationRoleSchema,
});

export type AcceptedInvitation = z.infer<typeof acceptedInvitationSchema>;

export const revokedInvitationSchema = z.object({
    id: entityIdSchema,
});

export type RevokedInvitation = z.infer<typeof revokedInvitationSchema>;

export const invitationListSchema = z.array(invitationSchema);

export const createdInvitationResponseSchema = successEnvelope(createdInvitationSchema);
export const invitationListResponseSchema = successEnvelope(invitationListSchema);
export const invitationPreviewResponseSchema = successEnvelope(invitationPreviewSchema);
export const acceptedInvitationResponseSchema = successEnvelope(acceptedInvitationSchema);
export const revokedInvitationResponseSchema = successEnvelope(revokedInvitationSchema);
